import {
  useContext,
  createContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import { api, errorTreatment } from "../../services/api";
import { dataComponent } from "../../pages/Dashboard/dataCharts";
import { LoadingContext } from "../../contexts/Loading/LoadingContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { AuthContext } from "../Authentication/AuthContext";
import moment from "moment-timezone";

interface DashboardContextData {
  addComponent: (add: AddComponentProps) => void;
  changesSaved: boolean;
  changeTitle: (title: ChangeTitleProps) => void;
  completeRequisition: boolean;
  componentsAdd: { id: string; name: string }[];
  componentProperty: (data: ComponentProperty) => boolean;
  dataComponents: Graphics[];
  dataComponentsInScreen: string[];
  filters: Dashboard["filters"];
  getSizesComponent: (id: SizesComponentProps) => ReturnSizesComponentProps;
  layouts: ConfigsFormarterGraphics["graphic"][];
  onLayoutChange: (layouts: ConfigsFormarterGraphics["graphic"][]) => void;
  reloadComponent: boolean;
  removeComponent: (remove: RemoveComponentProps) => void;
  renderComponent: (render: RenderComponentProps) => void;
  save: () => void;
  saveFilters: (filters: SaveFiltersProps) => void;
  setFontSize: (font: FontSizeProps) => void;
  titlePage: string;
  environment: number[];
  discountedDaysRoutes: (graphic_id: string) => Promise<string>;
  dueDateGraphic: (graphic_id: string) => Promise<number>;
  generateRedirectUrl: (graphic_id: string, data: any) => Promise<string>;
  getLayout: () => void;
  waitingRedirect: boolean;
}

interface SaveFiltersProps {
  couriers: string[];
  regions: string[];
  shippers: string[];
  clients: string[];
  routeTypes: string[];
}
interface AddComponentProps {
  id: string;
  dataSaved: {
    h: number;
    i: string;
    w: number;
    x: number;
    y: number;
    moved: boolean;
    static: boolean;
  } | null;
}
interface RemoveComponentProps {
  id: string;
}
interface RenderComponentProps {
  id: string;
}
interface ComponentProperty {
  id: string;
  property?: string;
}
interface SizesComponentProps {
  id: string;
}
interface FontSizeProps {
  id: string;
  width: number;
}
interface ReturnSizesComponentProps {
  height: number;
  width: number;
}
interface ChangeTitleProps {
  title: string;
}

interface DashboardProviderProps {
  children: ReactNode;
}
export interface Graphics {
  id: string;
  name: string;
  description: string;
  category: string;
  enabled: number;
  new: boolean;
  redirect: boolean;
  config: GraphicsConfig;
  component?: ReactNode;
  iconChart: {
    icon: ReactNode;
    title: string;
  };
  filter: boolean;
}

interface GraphicsConfig {
  h: number;
  i: string;
  w: number;
  x: number;
  y: number;
  minH: number;
  minW: number;
}

interface ConfigsFormarterGraphics {
  graphic:
    | {
        h: number;
        i: string;
        w: number;
        x: number;
        y: number;
        minH: number;
        minW: number;
      }
    | {
        h: number;
        i: string;
        w: number;
        x: number;
        y: number;
        moved: boolean;
        static: boolean;
      };
}

export interface Dashboard {
  id?: string;
  title: string;
  filters?: {
    regions: string[];
    shippers: string[];
    couriers: string[];
    clients: string[];
    routeTypes: string[];
  };
  graphics: {
    h: number;
    i: string;
    w: number;
    x: number;
    y: number;
    moved: boolean;
    static: boolean;
  }[];
}

export const DashboardContext = createContext({} as DashboardContextData);

export function DashboardProvider({ children }: DashboardProviderProps) {
  const { id, shortToken } = useParams();
  const [layouts, setLayouts] = useState<ConfigsFormarterGraphics["graphic"][]>(
    []
  );
  const [componentsAdd, setComponentsAdd] = useState<
    { id: string; name: string }[]
  >([]);
  const [filters, setFilters] = useState<Dashboard["filters"]>({
    regions: [],
    couriers: [],
    shippers: [],
    clients: [],
    routeTypes: [],
  });
  const [reloadComponent, setReloadComponent] = useState(false);
  const [completeRequisition, setCompleteRequisition] = useState(false);
  const [changesSaved, setChangesSaved] = useState(true);
  const [titlePage, setTitlePage] = useState("");
  const { changeVisibility } = useContext(LoadingContext);
  const { memcacheLogin, isAuthenticated, hasShortToken, getShortToken } =
    useContext(AuthContext);
  const [dataComponents, setDataComponents] = useState<Graphics[]>([]);
  const [dataComponentsInScreen, setDataComponentsInScreen] = useState<
    string[]
  >([""]);
  const [environment, setEnvironment] = useState<number[]>([]);
  const [waitingRedirect, setWaitingRedirect] = useState<boolean>(false);
  const navigate = useNavigate();

  const onLayoutChange = (layouts: ConfigsFormarterGraphics["graphic"][]) => {
    setReloadComponent(true);
    const updatedLayouts = layouts.map(
      (layout: ConfigsFormarterGraphics["graphic"]) => {
        const itemLayout =
          layouts.find(
            (item: ConfigsFormarterGraphics["graphic"]) => item.i === layout.i
          ) || layout;

        return itemLayout;
      }
    );
    setLayouts(updatedLayouts);
    setReloadComponent(false);
    setChangesSaved(false);
  };

  const removeComponent = ({ id }: RemoveComponentProps) => {
    dataComponents.map((component: Graphics) => {
      if (component.id === id) {
        const componentAdd = document.getElementById(`componentAdd${id}`);
        if (componentAdd) {
          componentAdd.style.display = "";
        }
        componentsAdd.map(
          (componentAdd: { id: string; name: string }, index) => {
            if (componentAdd.id === id) {
              componentsAdd.splice(index, 1);
            }
          }
        );
        const index = dataComponentsInScreen.indexOf(id);
        if (index > 0) {
          dataComponentsInScreen.splice(index, 1);
        }
        setComponentsAdd(componentsAdd);
        layouts.map((layout: ConfigsFormarterGraphics["graphic"], index) => {
          if (layout.i === id) {
            layouts.splice(index, 1);
          }
        });
        setLayouts(layouts);
        onLayoutChange(layouts);
      }
    });
  };

  const renderComponent = ({ id }: RenderComponentProps) => {
    let componentReturns: Graphics["component"];
    dataComponents.map((component: Graphics) => {
      if (component.id === id) {
        componentReturns = component.component;
      }
    });
    return componentReturns;
  };

  const addComponent = ({ id, dataSaved }: AddComponentProps) => {
    dataComponents.map((component: Graphics) => {
      if (component.id === id) {
        const componentAdd = document.getElementById(`componentAdd${id}`);
        if (componentAdd) {
          componentAdd.style.display = "none";
        }

        componentsAdd.push({ id, name: component.name });
        setComponentsAdd(componentsAdd);

        if (dataSaved) {
          layouts.push(dataSaved);
        } else {
          layouts.push(component.config);
        }
        setLayouts(layouts);
        onLayoutChange(layouts);
      }
    });
  };

  function saveFilters({
    couriers,
    regions,
    shippers,
    clients,
    routeTypes,
  }: SaveFiltersProps) {
    setFilters({
      couriers,
      regions,
      shippers,
      clients,
      routeTypes,
    });
    changeVisibility(true);
    Promise.all([
      api.patch(`/dashboards/${id}/configurations`, {
        graphics: layouts,
        filters: { couriers, regions, shippers, clients, routeTypes },
        title: titlePage,
      }),
    ])
      .then(() => {
        const oldLayouts = layouts;
        onLayoutChange([]);
        onLayoutChange(oldLayouts);
        changeVisibility(false);
        setChangesSaved(false);
      })
      .catch((error) => {
        errorTreatment(error);
        changeVisibility(false);
      });
  }

  const changeTitle = ({ title }: ChangeTitleProps) => {
    setTitlePage(title);
  };

  const getSizesComponent = ({ id }: SizesComponentProps) => {
    const fatherComponent = window.document.querySelector(`#${id}`)
      ?.parentNode as HTMLElement;
    return {
      height: parseInt(fatherComponent.style.height.slice(0, -2)),
      width: parseInt(fatherComponent.style.width.slice(0, -2)),
    };
  };

  const setFontSize = ({ id, width }: FontSizeProps) => {
    const component = window.document.querySelector(
      `#${id} .title-component`
    ) as HTMLElement;
    if (component) {
      component.style.fontSize = `${width / 20 + 4}px`;
    }
  };

  const save = () => {
    if (!titlePage || titlePage === "") {
      toast.error("Título é obrigatório!", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } else {
      changeVisibility(true);
      Promise.all([
        api.patch(`/dashboards/${id}/configurations`, {
          graphics: layouts,
          filters: filters,
          title: titlePage,
        }),
      ])
        .then(() => {
          changeVisibility(false);
          setChangesSaved(true);
          window.location.assign(`/dashboard/view/${id}`);
        })
        .catch((error) => {
          errorTreatment(error);
          changeVisibility(false);
        });
    }
  };

  const componentProperty = ({ id, property }: ComponentProperty) => {
    const propertyResult = dataComponents.find(
      (component: Graphics) => component.id === id
    );

    if (propertyResult && property && property === "redirect") {
      return propertyResult["redirect"];
    } else {
      return false;
    }
  };

  async function discountedDaysRoutes(graphic_id: string) {
    let days = "";
    const promises: Promise<unknown>[] = [];
    promises.push(
      api
        .get(
          `/optionsGraphics?code=discounted_days_route&graphic_id=${graphic_id}`
        )
        .then((documents) => {
          days = documents.data[0]?.value ?? "";
        })
        .catch((error) => {
          errorTreatment(error);
        })
    );
    await Promise.all(promises);
    return days;
  }

  async function getVisibilityRange(graphic_id: string) {
    let startDate = "";
    let endDate = "";
    const promises: Promise<unknown>[] = [];
    promises.push(
      api
        .get(`/visibilityRange?graphic_id=${graphic_id}`)
        .then((response) => {
          startDate = response.data?.startDate ?? "";
          endDate = response.data?.endDate ?? "";
        })
        .catch((error) => {
          errorTreatment(error);
        })
    );
    await Promise.all(promises);
    return { startDate, endDate };
  }

  // funcao que formata a url do redirecionamento dos graficos
  async function generateRedirectUrl(graphic_id: string, data: any) {
    try {
      setWaitingRedirect(true)
      let url = ``;
      if(data.redirectTowerToIndex){
        url = `${process.env.REACT_APP_COMPROVEI_URL}/documents/redirectTowerToIndex?limit=20&`;
      }
      if(data.redirectTowerToDelays){
        url = `${process.env.REACT_APP_COMPROVEI_URL}/routes/redirectTowerToDelays?`;
      }
      if(data.redirectTowerToRoutes){
        url = `${process.env.REACT_APP_COMPROVEI_URL}/routes/redirectTowerToRoutes?`;
      }
      if(data.redirectTowerToAnalytics){
        url = `${process.env.REACT_APP_COMPROVEI_URL}/routes/redirectTowerToAnalytics?`;
      }
      
      // formata qual data será usada
      if (data.date) {
        let date1 = "dateR1";
        let date2 = "dateR2";
        let date1Formated = "";
        let date2Formated = "";
        const { startDate, endDate } = await getVisibilityRange(graphic_id);
        if (data.dueRouteDate) {
          if(data.dueRouteDate === 'dateA'){
            date1 = "dateA1";
            date2 = "dateA2";
          }else if(data.dueRouteDate === 'dateR'){
            date1 = "dateR1";
            date2 = "dateR2";
          }else{
            const dueDate = await dueDateGraphic(graphic_id);
            date1 = dueDate ? "dateA1" : "dateR1";
            date2 = dueDate ? "dateA2" : "dateR2";
          }
        }
        if(data.otDate){
          date1 = "dateOT1";
          date2 = "dateOT2";
        }
        if(data.eDate){
          date1 = "dateE1";
          date2 = "dateE2";
        }
        if(data.slaDate){
          date1 = "date_sla1";
          date2 = "date_sla2";
        }
        if(data.slaDate || data.eDate){
          date1Formated = moment(startDate).format("DD/MM/YYYY");
          date2Formated = moment(endDate).format("DD/MM/YYYY");
        }else{
          date1Formated = startDate;
          date2Formated = endDate;
        }
        url += `${date1}=${date1Formated}&${date2}=${date2Formated}&`;
      }
      const redirectTowerToRoutes = data.redirectTowerToRoutes;
      const redirectTowerToDelays = data.redirectTowerToDelays;
      delete data.date;
      delete data.dueRouteDate;
      delete data.otDate;
      delete data.eDate;
      delete data.slaDate;
      delete data.redirectTowerToIndex;
      delete data.redirectTowerToDelays;
      delete data.redirectTowerToRoutes;
      delete data.redirectTowerToAnalytics;
      
      // remove demais parametros e adiciona todos os filtros enviados a função
      for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          url += `${key}=${data[key]}&`;
        }
      }
      // caso tiver filtro de tela e náo foi filtrado o parametro na 
      // funcao acima, adiciona o filtro à url
      if(filters?.clients && filters?.clients.length > 0){
        if(!data.client){
          if(redirectTowerToRoutes){
            url += `document_client_name=${filters.clients.join(',')}&`;
          }else{
            url += `client=${filters.clients.join(',')}&`;
          }
        }
      }
      if(filters?.couriers && filters?.couriers.length > 0){
        if(!data.cnpj_carrier){
          if(redirectTowerToRoutes){
            url += `route_courier=${filters.couriers.join(',')}&`;
          }else{
            url += `cnpj_carrier=${filters.couriers.join(',')}&`;
          }
        }
      }
      if(filters?.regions && filters?.regions.length > 0){
        if(!data.region){
          if(redirectTowerToRoutes){
            url += `route_region=${filters.regions.join(',')}&`;
          }else{
            url += `region=${filters.regions.join(',')}&`;
          }
        }
      }
      if(filters?.shippers && filters?.shippers.length > 0){
        if(!data.cnpj_shipper){
          if(redirectTowerToRoutes){
            url += `document_shipper=${filters.shippers.join(',')}&`;
          }else if(redirectTowerToDelays){
             url += `shipper=${filters.shippers.join(',')}&`;
          }else{
            url += `cnpj_shipper=${filters.shippers.join(',')}&`;
          }
        }
      }
      if(filters?.routeTypes && filters?.routeTypes.length > 0){
        url += `typeRoute=${filters.routeTypes.join(',')}&`;
      }
      setWaitingRedirect(false)
      return url;
    } catch (error) {
      setWaitingRedirect(false)
      return "";
    }
  }

  async function dueDateGraphic(graphic_id: string) {
    let date = 0;
    try {
      const documents = await api.get(
        `/optionsGraphics?code=due_date&graphic_id=${graphic_id}`
      );
      date = parseInt(documents.data[0]?.value) ?? 0;
      return date;
    } catch (error) {
      return date;
    }
  }

  useEffect(() => {
    if (shortToken && !isAuthenticated) {
      changeVisibility(true);
      getShortToken(shortToken).then((hasShortToken: boolean) => {
        if (!hasShortToken) {
          if (shortToken?.slice(0, 3) === "ctl") {
            memcacheLogin(shortToken);
          } else {
            navigate("/login");
          }
        }
      });
    } else if (!isAuthenticated) {
      navigate("/login");
    } else {
      getLayout();
    }
  }, []);

  function getLayout() {
    changeVisibility(true);
    dataComponent(id ? id : "").then(
      ([results, inScreen]: [Graphics[], string[]]) => {
        setDataComponents(results);
        setDataComponentsInScreen(inScreen);
      }
    );
    switch (process.env.NODE_ENV) {
      case "development":
        setEnvironment([0, 1, 2]);
        break;
      case "production":
        if (process.env.REACT_APP_URL === "https://tower-tcs.comprovei.com") {
          setEnvironment([1, 2]);
        } else {
          setEnvironment([1]);
        }
        break;
      default:
        break;
    }

    let routeConfiguration = "";
    if (hasShortToken) {
      routeConfiguration = `/dashboards/${id}/configurations?short_token=${shortToken}`;
    } else {
      routeConfiguration = `/dashboards/${id}/configurations`;
    }
    Promise.all([api.get(routeConfiguration)])
      .then(([configuration]: [{ data: Dashboard }]) => {
        setFilters(configuration.data.filters);
        if (configuration.data.graphics) {
          configuration.data.graphics.map(
            (graphic: {
              h: number;
              i: string;
              w: number;
              x: number;
              y: number;
              moved: boolean;
              static: boolean;
            }) => {
              addComponent({ id: graphic.i, dataSaved: graphic });
            }
          );
          setLayouts(configuration.data.graphics);
          onLayoutChange(configuration.data.graphics);
        }
        setTitlePage(configuration.data.title);
      })
      .catch((error: AxiosError) => {
        errorTreatment(error);
      })
      .finally(() => {
        changeVisibility(false);
        setCompleteRequisition(true);
        setChangesSaved(true);
      });
  }

  return (
    <DashboardContext.Provider
      value={{
        addComponent,
        changesSaved,
        changeTitle,
        completeRequisition,
        componentsAdd,
        componentProperty,
        dataComponents,
        dataComponentsInScreen,
        filters,
        getSizesComponent,
        layouts,
        onLayoutChange,
        reloadComponent,
        removeComponent,
        renderComponent,
        saveFilters,
        save,
        setFontSize,
        titlePage,
        environment,
        discountedDaysRoutes,
        dueDateGraphic,
        getLayout,
        generateRedirectUrl,
        waitingRedirect
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
}
